import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { NavigationProvider, useNavigation } from '../contexts/NavigationContext';
import Navigation from './Navigation';
import Header from './Header';
import BottomNavBar from './BottomNavBar';
import LegalFooter from './LegalFooter';
import StudentProfileSheet from './student/StudentProfileSheet';
import StudentNotificationsSheet from './student/StudentNotificationsSheet';
import FloatingRestTimer from './student/session/FloatingRestTimer';
import StudentAmbientBackground from './student/StudentAmbientBackground';
import CoachMobileHeader from './coach/CoachMobileHeader';
import CoachProfileSheet from './coach/CoachProfileSheet';
import useIsCoachMobile from '../hooks/useIsCoachMobile';
import { PullToRefreshProvider, PullToRefreshIndicator } from './PullToRefresh';
import useMediaQuery from '../hooks/useMediaQuery';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { OverlayModalProvider, useOverlayModal } from '../contexts/VideoModalContext';

// Loaded on large screens only: phones never download the sidebar.
const StudentDesktopNav = React.lazy(() => import('./student/StudentDesktopNav'));
// « Premiers pas » des nouveaux coachs : flottante, sur toutes les pages coach.
const CoachChecklist = React.lazy(() => import('./gettingStarted/CoachChecklist'));

// Context to track if WorkoutSessionExecution is open
const WorkoutSessionContext = createContext({
  isWorkoutSessionOpen: false,
  setIsWorkoutSessionOpen: () => { }
});

// eslint-disable-next-line react-refresh/only-export-components -- local context hook co-located with its provider component (established convention; dev-only HMR hint)
export const useWorkoutSession = () => useContext(WorkoutSessionContext);

// Context to hide main header when in chat thread (conversation open) - set by ChatPage
const HideMainHeaderInChatContext = createContext({ setHideMainHeaderInChatThread: () => { } });
// eslint-disable-next-line react-refresh/only-export-components -- local context hook co-located with its provider component (established convention; dev-only HMR hint)
export const useHideMainHeaderInChat = () => useContext(HideMainHeaderInChatContext);

/**
 * Fallback registry payload, module-level so its identity is stable — a fresh
 * object here would make `useSyncExternalStore` loop forever.
 */
const EMPTY_HEADER_ACTIONS = Object.freeze({
  openNotifications: () => { },
  openMenu: () => { },
  openChangelog: undefined,
  openDeleteAccount: undefined,
  hasUnseenChangelog: false,
  notifications: Object.freeze([]),
  onNotificationClick: undefined,
  onMarkAllAsRead: undefined,
  onToggleRead: undefined
});

/**
 * Coach analyses the student has not opened yet — what the badge on the
 * video-library tab counts. One `video_feedback` notification arrives per set
 * video, so distinct videoIds are the unit, not rows. `video_upload` rows are
 * addressed to the coach and never count here.
 */
const countUnreadAnalyses = (notifications) => {
  const ids = new Set();
  for (const n of notifications || []) {
    if (n.type === 'video_feedback' && n.read === false) ids.add(n.videoId ?? n.id);
  }
  return ids.size;
};

/** Same idea for the student sheet openers registered by <StudentSheetsHost>. */
const EMPTY_STUDENT_SHEETS = Object.freeze({
  openProfileSheet: () => { },
  openNotificationsSheet: () => { }
});

/**
 * Student routes that render their OWN Figma header and therefore must not
 * also get the app bar on top of it.
 *
 * Only the dashboard's header carries the hamburger + bell/avatar cluster
 * (`<StudentHeaderIcons>`, wired to `useHeaderActions()`); the other routes
 * here render a title-only header of
 * their own — from them, the drawer, notifications and profile are reached
 * one tap away via the bottom nav, not from the page header. Add a
 * redesigned student screen here and it inherits the "no app bar" treatment;
 * whether it also gets the hamburger/bell/avatar cluster is a separate,
 * per-page decision.
 */
const STUDENT_OWN_HEADER_ROUTES = ['/student/dashboard', '/student/history', '/student/videos', '/student/rankings', '/student/bilans'];
// The recap and match-sheet routes carry an id: matched by prefix below.
const STUDENT_OWN_HEADER_PREFIXES = ['/student/recap/', '/student/competitions/'];

/**
 * What `Header` publishes to pages that render their own chrome — the student
 * screens listed in `STUDENT_OWN_HEADER_ROUTES`, whose Figma header replaces
 * the app bar.
 *
 * Two things travel through here:
 *  - the openers for the overlays `Header` owns (NotificationSidebar and
 *    MobileNavigationDrawer);
 *  - the notifications themselves plus their mutation callbacks, so the
 *    dashboard's own bottom sheet renders the SAME single fetch instead of
 *    opening a second one.
 *
 * `Header` stays the single owner of the overlay state and of the notification
 * fetch; it only *registers* here. Lifting that state into MainLayout would
 * re-render the whole layout subtree (dashboard included) on every open/close
 * and on every notification, which the ref-based registry avoids: the context
 * value is stable, so consumers never re-render because of it.
 *
 * Because a plain ref cannot tell anyone the notifications changed, the
 * registry doubles as a tiny external store — `subscribeHeaderActions` +
 * `getHeaderActions`, read through `useHeaderNotifications()`. Only the
 * components that call that hook re-render when Header re-registers; every
 * `useHeaderActions()` consumer stays untouched.
 *
 * `openProfileSheet` / `openNotificationsSheet` ride along the same channel for
 * the same reason. The two student bottom sheets are mounted once, in
 * <StudentSheetsHost> below, and that host owns their open/close booleans;
 * MainLayout only keeps a ref to its openers. Holding those booleans in
 * MainLayout itself would re-render every page on each open and close.
 */
const HeaderActionsContext = createContext({
  openNotifications: () => { },
  openMenu: () => { },
  openProfileSheet: () => { },
  openNotificationsSheet: () => { },
  registerHeaderActions: () => { },
  registerStudentSheets: () => { },
  subscribeHeaderActions: () => () => { },
  getHeaderActions: () => EMPTY_HEADER_ACTIONS
});
// eslint-disable-next-line react-refresh/only-export-components -- local context hook co-located with its provider component (established convention; dev-only HMR hint)
export const useHeaderActions = () => useContext(HeaderActionsContext);

/**
 * Subscribe to the notification slice `Header` publishes. Returns the whole
 * registry payload: `{ notifications, onNotificationClick, onMarkAllAsRead,
 * onToggleRead, ... }`.
 */
// eslint-disable-next-line react-refresh/only-export-components -- local context hook co-located with its provider component (established convention; dev-only HMR hint)
export const useHeaderNotifications = () => {
  const { subscribeHeaderActions, getHeaderActions } = useContext(HeaderActionsContext);
  return useSyncExternalStore(subscribeHeaderActions, getHeaderActions, getHeaderActions);
};

/**
 * The two student bottom sheets, mounted ONCE for the whole app so any student
 * screen can open them — previously they lived inside StudentDashboard and were
 * unreachable from anywhere else.
 *
 * The open/close state lives here, in this leaf, and not in MainLayout: this
 * component re-renders on every open, close and notification update, and it
 * renders nothing but the two sheets (both `null` while closed). MainLayout,
 * which wraps every page, never re-renders for any of it — it only holds a ref
 * to the openers, exactly like the header-actions registry above.
 *
 * The sheets are presentational: the notifications and their callbacks come
 * from the SINGLE fetch <Header> owns, read through `useHeaderNotifications()`.
 */
const StudentSheetsHost = () => {
  const { registerStudentSheets } = useContext(HeaderActionsContext);
  const headerNotifications = useHeaderNotifications();
  const [isProfileSheetOpen, setIsProfileSheetOpen] = useState(false);
  const [isNotificationsSheetOpen, setIsNotificationsSheetOpen] = useState(false);

  useEffect(() => {
    registerStudentSheets({
      openProfileSheet: () => setIsProfileSheetOpen(true),
      openNotificationsSheet: () => setIsNotificationsSheetOpen(true)
    });
    return () => registerStudentSheets(null);
  }, [registerStudentSheets]);

  const handleProfileSheetClose = useCallback(() => setIsProfileSheetOpen(false), []);
  const handleNotificationsSheetClose = useCallback(() => setIsNotificationsSheetOpen(false), []);
  // Close the sheet before handing the click to Header (it may open the video
  // modal, which must not sit behind the sheet's backdrop).
  const handleNotificationClick = useCallback((notif) => {
    setIsNotificationsSheetOpen(false);
    headerNotifications.onNotificationClick?.(notif);
  }, [headerNotifications]);

  return (
    <>
      {/* Feuille de profil — ouverte par l'avatar de l'en-tête Figma. */}
      <StudentProfileSheet isOpen={isProfileSheetOpen} onClose={handleProfileSheetClose} />
      {/* Feuille de notifications — ouverte par la cloche de l'en-tête Figma. */}
      <StudentNotificationsSheet
        isOpen={isNotificationsSheetOpen}
        onClose={handleNotificationsSheetClose}
        notifications={headerNotifications.notifications}
        onNotificationClick={handleNotificationClick}
        onMarkAllAsRead={headerNotifications.onMarkAllAsRead}
      />
    </>
  );
};

/**
 * Coach chrome on phones: the student-style header plus the coach profile
 * sheet its avatar opens. A leaf for the same reason as StudentSheetsHost —
 * it re-renders on every notification and sheet toggle, MainLayout doesn't.
 * Only mounted below md (`isCoachMobile`), so the coach desktop keeps Header's
 * own bar.
 */
const CoachMobileChrome = () => {
  const { user } = useAuth();
  const headerNotifications = useHeaderNotifications();
  const { notifications, hasUnseenChangelog, openChangelog, openDeleteAccount } = headerNotifications;
  const { newProspectsCount } = useNavigation();
  const hasNewCandidatures = newProspectsCount > 0;
  const [isProfileSheetOpen, setIsProfileSheetOpen] = useState(false);
  // The bell opens the student's notifications sheet (same design), not
  // Header's desktop side panel.
  const [isNotificationsSheetOpen, setIsNotificationsSheetOpen] = useState(false);
  const hasUnread = useMemo(() => (notifications || []).some((n) => !n.read), [notifications]);
  const openProfileSheet = useCallback(() => setIsProfileSheetOpen(true), []);
  const closeProfileSheet = useCallback(() => setIsProfileSheetOpen(false), []);
  const openNotificationsSheet = useCallback(() => setIsNotificationsSheetOpen(true), []);
  const closeNotificationsSheet = useCallback(() => setIsNotificationsSheetOpen(false), []);
  // Close the sheet before handing the click to Header (it may open the video
  // page, which must not sit behind the sheet's backdrop) — as StudentSheetsHost.
  const handleNotificationClick = useCallback((notif) => {
    setIsNotificationsSheetOpen(false);
    headerNotifications.onNotificationClick?.(notif);
  }, [headerNotifications]);

  return (
    <>
      <StudentNotificationsSheet
        isOpen={isNotificationsSheetOpen}
        onClose={closeNotificationsSheet}
        notifications={notifications}
        onNotificationClick={handleNotificationClick}
        onMarkAllAsRead={headerNotifications.onMarkAllAsRead}
        role={user?.role || 'coach'}
      />
      <CoachMobileHeader
        onBellClick={openNotificationsSheet}
        onAvatarClick={openProfileSheet}
        hasUnread={hasUnread}
        hasAvatarDot={hasUnseenChangelog || hasNewCandidatures}
      />
      <CoachProfileSheet
        isOpen={isProfileSheetOpen}
        onClose={closeProfileSheet}
        onOpenChangelog={openChangelog}
        hasUnseenChangelog={hasUnseenChangelog}
        hasNewCandidatures={hasNewCandidatures}
        onDeleteAccount={openDeleteAccount}
      />
    </>
  );
};

const BottomNavBarWrapper = ({ hideInChatThread = false }) => {
  const { isModalOpen } = useOverlayModal();
  const { isWorkoutSessionOpen } = useWorkoutSession();
  // Read the notification slice HERE, in this leaf, so MainLayout itself never
  // re-renders on a notification update — same reasoning as StudentSheetsHost.
  const { notifications } = useHeaderNotifications();
  const newAnalysesCount = useMemo(() => countUnreadAnalyses(notifications), [notifications]);
  if (isWorkoutSessionOpen || isModalOpen || hideInChatThread) return null;
  return <BottomNavBar newAnalysesCount={newAnalysesCount} />;
};

/**
 * Student sidebar from 1024px. A leaf for the same reason as the wrapper
 * above. Hidden during a workout, like the pill: the session screen is a focus
 * mode with its own exit.
 */
const StudentDesktopNavWrapper = () => {
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const { isWorkoutSessionOpen } = useWorkoutSession();
  const { notifications } = useHeaderNotifications();
  const { openProfileSheet, openNotificationsSheet } = useHeaderActions();
  const newAnalysesCount = useMemo(() => countUnreadAnalyses(notifications), [notifications]);
  const unreadNotificationsCount = useMemo(
    () => (notifications || []).filter((n) => n.read === false).length,
    [notifications]
  );
  if (!isDesktop || isWorkoutSessionOpen) return null;
  return (
    <React.Suspense fallback={null}>
      <StudentDesktopNav
        newAnalysesCount={newAnalysesCount}
        unreadNotificationsCount={unreadNotificationsCount}
        onOpenNotifications={openNotificationsSheet}
        onOpenProfile={openProfileSheet}
      />
    </React.Suspense>
  );
};

// Messagerie flottante du coach (ordinateur), hors page Messages : chargée à
// part, jamais sur téléphone (la barre du bas y mène déjà aux messages).
const MessagesDock = React.lazy(() => import('./chat/MessagesDock'));
const MessagesDockWrapper = () => {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  if (!isDesktop) return null;
  return (
    <React.Suspense fallback={null}>
      <MessagesDock />
    </React.Suspense>
  );
};

const MainLayout = ({ children }) => {
  const location = useLocation();
  const { user } = useAuth();
  const isChatPage = location.pathname.startsWith('/chat');
  // Only decides whether the app bar is drawn.
  const hasOwnStudentHeader =
    user?.role === 'student' &&
    (STUDENT_OWN_HEADER_ROUTES.includes(location.pathname) ||
      STUDENT_OWN_HEADER_PREFIXES.some((prefix) => location.pathname.startsWith(prefix)));
  const isExercisePage = location.pathname === '/coach/exercises' || location.pathname === '/admin/exercises';
  const isVideoLibraryPage = location.pathname === '/coach/videotheque';
  const dashboardSearchParams = new URLSearchParams(location.search);
  const isCoachClientsMainPage = location.pathname === '/coach/dashboard';
  const isCoachClientsListView = isCoachClientsMainPage && !dashboardSearchParams.get('studentId');
  // A student's page under Clients draws its own phone header (back arrow +
  // name, StudentDetailView), so the coach header stays off there.
  const isCoachStudentDetail = isCoachClientsMainPage && Boolean(dashboardSearchParams.get('studentId'));
  // A competition (and its match sheets) has its own back header on the phone.
  const isCoachCompetitionPage = /^\/coach\/competitions\/.+/.test(location.pathname);
  const isCoachStudentAnalyseTab =
    isCoachClientsMainPage &&
    Boolean(dashboardSearchParams.get('studentId')) &&
    dashboardSearchParams.get('tab') === 'analyse';
  const [isWorkoutSessionOpen, setIsWorkoutSessionOpen] = useState(false);
  const [hideMainHeaderInChatThread, setHideMainHeaderInChatThread] = useState(false);
  const isStudent = user?.role === 'student';
  // Coach below md: student look (background, header, nav). Always false on
  // the coach desktop, which keeps everything below exactly as it was.
  const isCoachMobile = useIsCoachMobile();
  // Pages that use the student-style shell (page fade on route change).
  const usesStudentShell = isStudent || isCoachMobile;
  const scrollRef = useRef(null);
  const pageKey = usesStudentShell ? location.pathname : 'page';

  const headerActionsRef = useRef(EMPTY_HEADER_ACTIONS);
  const headerActionsListenersRef = useRef(new Set());
  const registerHeaderActions = useCallback((actions) => {
    headerActionsRef.current = actions || EMPTY_HEADER_ACTIONS;
    // Fired from Header's effect, never during render. Header re-registers in
    // place and only publishes the empty fallback when it unmounts, so
    // subscribers never see a transient "no notifications" value.
    headerActionsListenersRef.current.forEach((listener) => listener());
  }, []);
  const subscribeHeaderActions = useCallback((listener) => {
    headerActionsListenersRef.current.add(listener);
    return () => headerActionsListenersRef.current.delete(listener);
  }, []);
  const getHeaderActions = useCallback(() => headerActionsRef.current, []);
  const openNotifications = useCallback(() => headerActionsRef.current?.openNotifications?.(), []);
  const openMenu = useCallback(() => headerActionsRef.current?.openMenu?.(), []);

  // Second, independent slot so <StudentSheetsHost> and <Header> never clobber
  // each other's registration. No listeners here: nobody needs to observe the
  // openers, they are only ever called.
  const studentSheetsRef = useRef(EMPTY_STUDENT_SHEETS);
  const registerStudentSheets = useCallback((actions) => {
    studentSheetsRef.current = actions || EMPTY_STUDENT_SHEETS;
  }, []);
  const openProfileSheet = useCallback(() => studentSheetsRef.current?.openProfileSheet?.(), []);
  const openNotificationsSheet = useCallback(
    () => studentSheetsRef.current?.openNotificationsSheet?.(),
    []
  );

  const headerActions = useMemo(
    () => ({
      openNotifications,
      openMenu,
      openProfileSheet,
      openNotificationsSheet,
      registerHeaderActions,
      registerStudentSheets,
      subscribeHeaderActions,
      getHeaderActions
    }),
    [
      openNotifications,
      openMenu,
      openProfileSheet,
      openNotificationsSheet,
      registerHeaderActions,
      registerStudentSheets,
      subscribeHeaderActions,
      getHeaderActions
    ]
  );

  return (
    <NavigationProvider>
    <WorkoutSessionContext.Provider value={{ isWorkoutSessionOpen, setIsWorkoutSessionOpen }}>
      <HideMainHeaderInChatContext.Provider value={{ setHideMainHeaderInChatThread }}>
        <HeaderActionsContext.Provider value={headerActions}>
        <OverlayModalProvider>
        <PullToRefreshProvider>
          <div className="h-dvh bg-background text-foreground flex overflow-hidden w-full relative" style={{ backgroundColor: '#0a0a0a' }}>
            {/* Coach-only ambient background. Every student screen paints its
                own <StudentAmbientBackground> (the dashboard's stack), so for
                students this layer was a second, invisible-but-costly copy
                underneath — and its INLINE backdrop-filters (no -webkit- twin
                on the conic layers, 50px vs 100px on the screen blur) rendered
                differently on iOS than in Chrome. Dropping it for students
                makes the phone match the desktop preview and halves the
                backdrop-filter work WebKit has to do. */}
            {/* Coach on a phone: the student background instead (same image,
                status-bar band and copper glow). JS-gated, not just hidden:
                StudentAmbientBackground also repaints the status bar. */}
            {isCoachMobile && <StudentAmbientBackground />}
            {!isStudent && !isCoachMobile && (
              <>
                {/* Image de fond */}
                <div
                  style={{
                    position: 'fixed',
                    top: '0',
                    left: '0',
                    width: '100vw',
                    height: '100dvh',
                    backgroundImage: 'url(/background.jpg)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    zIndex: 1,
                    backgroundColor: '#0a0a0a'
                  }}
                />

                {/* Layer blur sur l'écran. Le flou passe par les classes
                    kaiylo-ambient-* (index.css) : rayons allégés et animation
                    coupée sur écran tactile, sinon une tablette Android
                    sature sa mémoire graphique (contenu des modales qui
                    s'efface au défilement, onglet qui se recharge). */}
                <div
                  className="kaiylo-ambient-screen-blur"
                  style={{
                    position: 'fixed',
                    top: '0',
                    left: '0',
                    width: '100vw',
                    height: '100dvh',
                    backgroundColor: 'rgba(0, 0, 0, 0.01)',
                    zIndex: 6,
                    pointerEvents: 'none',
                    opacity: 1
                  }}
                />

                {/* Gradient conique Figma - partie droite */}
                <div
                  className="kaiylo-ambient-conic-blur"
                  style={{
                    position: 'absolute',
                    top: '-175px',
                    left: '0',
                    transform: 'translateY(-50%)',
                    width: '50vw',
                    height: '600px',
                    borderRadius: '0',
                    background: 'conic-gradient(from 90deg at 0% 50%, #FFF 0deg, rgba(255, 255, 255, 0.95) 5deg, rgba(255, 255, 255, 0.9) 10deg,rgb(35, 38, 49) 23.50555777549744deg, rgba(0, 0, 0, 0.51) 105.24738073348999deg, rgba(18, 2, 10, 0.18) 281.80317878723145deg, rgba(9, 0, 4, 0.04) 330.0637102127075deg, rgba(35, 70, 193, 0.15) 340deg, rgba(35, 70, 193, 0.08) 350deg, rgba(35, 70, 193, 0.03) 355deg, rgba(35, 70, 193, 0.01) 360.08655548095703deg, rgba(0, 0, 0, 0.005) 360deg)',
                    boxShadow: 'none',
                    filter: 'brightness(1.25)',
                    zIndex: 5,
                    pointerEvents: 'none',
                    opacity: 0.75,
                    animation: 'organicGradient 15s ease-in-out infinite'
                  }}
                />

                {/* Gradient conique Figma - partie gauche (symétrie axiale) */}
                <div
                  className="kaiylo-ambient-conic-blur"
                  style={{
                    position: 'absolute',
                    top: '-175px',
                    left: '50vw',
                    transform: 'translateY(-50%) scaleX(-1)',
                    width: '50vw',
                    height: '600px',
                    borderRadius: '0',
                    background: 'conic-gradient(from 90deg at 0% 50%, #FFF 0deg, rgba(255, 255, 255, 0.95) 5deg, rgba(255, 255, 255, 0.9) 10deg,rgb(35, 38, 49) 23.50555777549744deg, rgba(0, 0, 0, 0.51) 105.24738073348999deg, rgba(18, 2, 10, 0.18) 281.80317878723145deg, rgba(9, 0, 4, 0.04) 330.0637102127075deg, rgba(35, 70, 193, 0.15) 340deg, rgba(35, 70, 193, 0.08) 350deg, rgba(35, 70, 193, 0.03) 355deg, rgba(35, 70, 193, 0.01) 360.08655548095703deg, rgba(0, 0, 0, 0.005) 360deg)',
                    boxShadow: 'none',
                    filter: 'brightness(1.25)',
                    zIndex: 5,
                    pointerEvents: 'none',
                    opacity: 0.75,
                    animation: 'organicGradient 15s ease-in-out infinite 1.5s'
                  }}
                />
              </>
            )}

            <Navigation />
            {isStudent && <StudentDesktopNavWrapper />}
            {/* lg:pl = the student sidebar's width (StudentDesktopNav). */}
            <main className={`flex-1 flex flex-col overflow-hidden w-full relative z-10 ${isStudent && !isWorkoutSessionOpen ? 'lg:pl-[232px]' : ''}`} style={{ gap: 0 }}>
              {!isWorkoutSessionOpen && (hideMainHeaderInChatThread ? (
                <div className="hidden md:block main-layout-header">
                  <Header hideBar={hasOwnStudentHeader} registerActions={registerHeaderActions} />
                </div>
              ) : (
                /* `hideBar` on the redesigned student screens: those pages render
                   the Figma header (title/date + bell + avatar) themselves, so the
                   app bar would be a second, duplicate header. Header stays mounted
                   so its NotificationSidebar / MobileNavigationDrawer / video modal
                   keep working — only the visible bar is dropped. */
                <Header hideBar={hasOwnStudentHeader || isCoachMobile} registerActions={registerHeaderActions} />
              ))}
              {/* Coach phone header (title + bell + avatar), in place of the app
                  bar that `hideBar` drops just above. */}
              {isCoachMobile && !isWorkoutSessionOpen && !hideMainHeaderInChatThread && !isCoachStudentDetail && !isCoachCompetitionPage && (
                <CoachMobileChrome />
              )}
              {/* Student page transition: the scroll container is keyed on the
                  route, so each navigation remounts it (scroll back to top) and
                  replays the `kaiylo-page-enter` fade (src/index.css). Opacity
                  only, no transform — a transform here would turn the container
                  into the containing block of the pages' `position: fixed`
                  background layers for the duration of the animation.
                  `scrollbar-gutter: stable` (students only): when a page flips
                  between scrolling and not — the video library's Terminé vs
                  Ressources tabs, a 5- vs 6-row month on the planning — the
                  desktop scrollbar would otherwise appear and vanish, and every
                  element would lurch sideways by its width. Overlay scrollbars
                  on phones take no space, so this is a no-op there. */}
              {/* Tirer pour actualiser : geste écouté sur ce conteneur (voir
                  PullToRefresh), pas de wrapper ni de rendu pendant le geste. */}
              <div ref={scrollRef} key={pageKey} className={`flex-1 relative z-10 overflow-x-hidden min-h-0 ${isStudent || isCoachMobile ? 'kaiylo-page-enter [scrollbar-gutter:stable]' : ''} ${isChatPage ? (hideMainHeaderInChatThread || isCoachMobile ? 'p-0 overflow-hidden' : 'p-0 overflow-hidden pb-16 md:pb-0') : isExercisePage || isVideoLibraryPage || isCoachStudentAnalyseTab ? 'pt-0 pb-0 overflow-hidden flex flex-col w-full min-h-0 dashboard-scrollbar' : 'pt-0 pb-0 overflow-y-auto dashboard-scrollbar w-full'}`} style={{ marginTop: 0, paddingTop: 0, color: 'rgba(160, 19, 19, 0)' }}>
                {children}
              </div>
              {/* Legal footer — only on coach "Clients" main page */}
              {!isChatPage && !isWorkoutSessionOpen && isCoachClientsListView && (
                <div className="hidden sm:block">
                  <LegalFooter />
                </div>
              )}
            </main>
          </div>
          <PullToRefreshIndicator
            containerRef={scrollRef}
            resetKey={pageKey}
            enabled={!isWorkoutSessionOpen && !isChatPage}
          />
          {/* Pas de barre sur le récap de séance : écran de fin, plein écran,
              qu'on quitte par son propre bouton retour. */}
          <BottomNavBarWrapper hideInChatThread={hideMainHeaderInChatThread || location.pathname.startsWith('/student/recap/')} />

          {/* Feuilles étudiant (profil / notifications) — montées une seule fois
              ici pour que toutes les pages élève y aient accès via
              `useHeaderActions()`. */}
          {isStudent && <StudentSheetsHost />}

          {/* Repos en cours, visible sur toutes les pages élève. */}
          {isStudent && <FloatingRestTimer />}

          {/* Coin bas droit : le bouton Messages, avec la checklist « Premiers pas »
              des nouveaux coachs juste au-dessus. */}
          {user?.role === 'coach' && (
            <React.Suspense fallback={null}>
              <CoachChecklist />
            </React.Suspense>
          )}
          {!isStudent && !isChatPage && <MessagesDockWrapper />}
        </PullToRefreshProvider>
        </OverlayModalProvider>
        </HeaderActionsContext.Provider>
      </HideMainHeaderInChatContext.Provider>
    </WorkoutSessionContext.Provider>
    </NavigationProvider>
  );
};

export default MainLayout;

